import {timezones} from '@cl/shared';
import formatSelectedItemsContext from 'common/src/modules/shared/copilot/utils/formatSelectedItemsContext';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {UTCAbbreviation, UTCTimezoneObject} from 'common/src/modules/timezones/timezones.consts';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import {CLStore} from 'common/src/redux/store';
import convertDatesFromUrlFilters from 'common/src/utils/urlFiltersUtils/convertDatesFromUrlFilters';
import getUrlQueryParamValue from 'common/src/utils/urlUtils/getUrlQueryParamValue/getUrlQueryParamValue';
import _find from 'lodash/find';
import {DateTime} from 'luxon';
import {SlimCampaign} from '../../campaigns.types';
import {CampaignSearchParamKey} from '../../store/campaignsLibrary/campaignsLibrary.enums';
import {AIFilter} from '../../store/campaignsLibrary/campaignsLibrary.types';

type CampaignsLibraryContext = {
  aiFilter: AIFilter;
  campaigns: SlimCampaign[];
  selectedCampaignIds: number[];
  metricsDateRange: {
    startDate: DateTime;
    endDate: DateTime;
    selectedTimezone: TimezoneObject;
  };
};

export default function campaignsLibraryContext(
  store: CLStore,
): AgentContext<CampaignsLibraryContext> {
  return {
    title: 'Campaigns library',
    getter: (): CampaignsLibraryContext => {
      const {campaigns, selectedCampaignIds, aiFilter} = store.getState().campaignsLibrary;
      const date = getUrlQueryParamValue<string>(CampaignSearchParamKey.Date);
      const timezone =
        getUrlQueryParamValue<string>(CampaignSearchParamKey.Timezone) ?? UTCAbbreviation;
      const info = timezones.dateTimeInfoSubject.data;

      let dateRangeFilters: {
        startDate: DateTime;
        endDate: DateTime;
        selectedTimezone: TimezoneObject;
      } = {
        startDate: DateTime.now(),
        endDate: DateTime.now(),
        selectedTimezone: UTCTimezoneObject,
      };

      if (date) {
        const {startDate, endDate} = convertDatesFromUrlFilters(date);
        dateRangeFilters = {
          startDate,
          endDate,
          selectedTimezone: _find(
            info?.timezoneMapper,
            ({abbr}: TimezoneObject) => abbr === timezone,
          ) as TimezoneObject,
        };
      }

      return {
        aiFilter,
        campaigns,
        selectedCampaignIds,
        metricsDateRange: dateRangeFilters,
      };
    },
    formatter: ({currentContext}) => {
      let formatterMessage = `We have ${currentContext.campaigns.length} campaigns.`;

      const selectedCampaignsForContext = currentContext.campaigns.filter((campaign) =>
        currentContext.aiFilter?.aiFilteredIds?.length
          ? currentContext.aiFilter.aiFilteredIds.includes(campaign.id)
          : currentContext.selectedCampaignIds.includes(campaign.id),
      );

      const selectedCampaignsMessage = formatSelectedItemsContext({
        items: selectedCampaignsForContext,
        itemTypeName: 'campaign',
      });

      formatterMessage += ` ${selectedCampaignsMessage}`;

      if (currentContext.metricsDateRange?.startDate && currentContext.metricsDateRange?.endDate) {
        formatterMessage += `\nThe metrics date range is from ${currentContext.metricsDateRange.startDate.toFormat('yyyy-MM-dd')} to ${currentContext.metricsDateRange.endDate.toFormat('yyyy-MM-dd')}`;

        if (currentContext.metricsDateRange.selectedTimezone?.abbr) {
          formatterMessage += ` in ${currentContext.metricsDateRange.selectedTimezone.abbr}.`;
        }
      } else {
        formatterMessage += ` No metrics date range is set.`;
      }

      return formatterMessage;
    },
  };
}
